import { useEffect, useRef, useState } from "react"
import type { ReactNode } from "react"
import classnames from "classnames"
import parse from "html-react-parser"

import chevronUp from "images/layout/chevron-up.svg?raw"
import chevronDown from "images/layout/chevron-down.svg?raw"

import styles from "./options_panel.module.scss"

interface Props {
  children: ReactNode
}

// Distinct per panel so aria-controls can never point at another instance.
let panelCount = 0

// A fixed column beside the editor on desktop; below the split it becomes a
// drawer pinned to the bottom edge that grows upward over the editor.
export default function OptionsPanel({ children }: Props) {
  const [open, setOpen] = useState(false)
  const [bodyId] = useState(() => `filter-code-options-${++panelCount}`)
  const toggleRef = useRef<HTMLButtonElement>(null)

  // Below the split the open drawer covers the editor; Escape closes it and
  // hands focus back to the control that opened it.
  useEffect(() => {
    if (!open) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      setOpen(false)
      toggleRef.current?.focus()
    }

    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [open])

  return (
    <aside className={classnames(styles.panel, { [styles.open]: open })}>
      <header className={styles.header}>
        <h2 className={classnames("text-h4", styles.title)}>Filter options</h2>
        <button
          ref={toggleRef}
          type="button"
          className={classnames("icon-button", styles.toggle)}
          aria-expanded={open}
          aria-controls={bodyId}
          aria-label={open ? "Hide filter options" : "Show filter options"}
          onClick={() => setOpen(current => !current)}
        >
          {parse(open ? chevronDown : chevronUp)}
        </button>
      </header>
      <div id={bodyId} className={styles.body}>
        {children}
      </div>
    </aside>
  )
}
